'use client';

import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import { useAtomValue } from 'jotai';
import { ArticleSection, IBlogPostResponse } from '@sapientpro/sapientpro-data-models';
import clientDeviceSize, { ScreenSize } from '../../../store/deviceScreenSize';
import { theme, Theme } from '../../../store/theme';
import styles from './Article.module.scss';
import { sectionProgress } from './articleState';

const getProgressBarBg = (appTheme: Theme, sectionProgressValue: number) => (
  appTheme === Theme.DARK
    ? `linear-gradient(to right, ${getComputedStyle(document.documentElement)
      .getPropertyValue('--primary-500')} ${sectionProgressValue * 100}%, ${getComputedStyle(document.documentElement)
      .getPropertyValue('--secondary-500')} ${sectionProgressValue * 100}%)`
    : `linear-gradient(to right, ${getComputedStyle(document.documentElement)
      .getPropertyValue('--primary-900')} ${sectionProgressValue * 100}%, ${getComputedStyle(document.documentElement)
      .getPropertyValue('--secondary-50')} ${sectionProgressValue * 100}%)`
);

type Section = {
  id: string;
  text: string;
  element: HTMLElement;
};

const extractHeadingsFromHtml = (html: string) => {
  const headings: Section[] = [];
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  const h2Elements = tempDiv.querySelectorAll('h2');

  h2Elements.forEach((h2, index) => {
    headings.push({
      id: `heading-${index + 1}`,
      text: h2.textContent || '',
      element: h2,
    });
  });
  return headings;
};

type ArticleNavigationProps = {
  data: IBlogPostResponse,
  activeSection: number | null,
  onClick: (id: number) => void,
};

const ArticleNavigation = ({ data, activeSection, onClick }: ArticleNavigationProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [headings, setHeadings] = useState<Section[]>([]);
  const sectionProgressValue = useAtomValue(sectionProgress);
  const appTheme = useAtomValue(theme);
  const deviceSize = useAtomValue<ScreenSize>(clientDeviceSize);
  const isMobileDevice = deviceSize === ScreenSize.MOBILE;

  useEffect(() => {
    if (data.post?.resultHtml) {
      const extractedHeadings = extractHeadingsFromHtml(data.post.resultHtml);

      setHeadings(extractedHeadings);
    }
  }, [data.post?.resultHtml]);

  const toggleMenu = () => setIsOpen(!isOpen);

  const renderMenuItems = () => {
    if (data.post?.resultHtml && headings.length > 0) {
      return headings.map((heading, index) => {
        const isActive = activeSection === index + 1;

        return (
          <button
            type='button'
            key={heading.id}
            className={classnames(styles.articleAnchor, {
              [styles.active]: isActive,
              [styles.hasProgress]: isActive && deviceSize > ScreenSize.TABLET_LANDSCAPE,
            })}
            onClick={() => {
              onClick(index + 1);
            }}
          >
            <span
              className={classnames(styles.sectionNumber, {
                [styles.sectionNumber_active]: isActive,
              })}
            >
              {index + 1}
            </span>
            <p>{heading.text}</p>
            {isActive && deviceSize > ScreenSize.TABLET_LANDSCAPE && (
              <div
                className={styles.progress}
                style={{ background: getProgressBarBg(appTheme, sectionProgressValue) }}
              >
                <div
                  style={{ left: `${sectionProgressValue * 100}%` }}
                  className={styles.progress__marker}
                />
              </div>
            )}
          </button>
        );
      });
    } else if (!data.post?.resultHtml) {
      return data.post?.content?.map((section, index) => {
        const isActive = section?.id ? activeSection === +section.id : false;

        return (
          <button
            type='button'
            key={section?.id}
            className={classnames(styles.articleAnchor, {
              [styles.active]: isActive,
            })}
            onClick={() => onClick(+section.id)}
          >
            <span
              className={classnames(styles.sectionNumber, {
                [styles.sectionNumber_active]: isActive,
              })}
            >
              {index + 1}
            </span>
            <p>{(section as ArticleSection)?.heading}</p>
            {isActive && deviceSize > ScreenSize.TABLET_LANDSCAPE && (
            <div
              className={styles.progress}
              style={{ background: getProgressBarBg(appTheme, sectionProgressValue) }}
            >
              <div
                style={{ left: `${sectionProgressValue * 100}%` }}
                className={styles.progress__marker}
              />
            </div>
            )}
          </button>
        );
      });
    }
  };

  return (
    (isOpen || !isMobileDevice) ? (
      <nav className={classnames(styles.articleMenuWrapper, {
        [styles.articleMenuWrapper_mobile]: isMobileDevice,
        [styles.articleMenuWrapper_openOnMobile]: isMobileDevice && isOpen,
      })}
      >
        {isMobileDevice && (<p className={styles.navigationSubtitle}>Key Takeaways</p>)}
        <div className={styles.articleMenu}>
          {renderMenuItems()}
        </div>
        {isMobileDevice && (
          <button
            type='button'
            onClick={toggleMenu}
            className={classnames(styles.arrowIcon, { [styles.arrowIcon_open]: isOpen })}
          >
            <svg>
              <use
                href='/media/angle_down.svg#angleDown'
                xlinkHref='/media/angle_down.svg#angleDown'
              />
            </svg>
          </button>
        )}
      </nav>
    ) : (
      <button
        type='button'
        className={classnames(styles.articleMenuWrapper, styles.articleMenuWrapper_mobile)}
        onClick={toggleMenu}
      >
        <p className={styles.navigationSubtitle}>Key Takeaways</p>
        <div className={styles.listItemWrapper}>
          {data.post?.content?.[0] && (
            <div
              className={classnames(styles.articleAnchorForMobileToggleButton, styles.articleAnchorForMobileToggleButton_active)}
            >
              <span
                className={classnames(styles.sectionNumber, styles.sectionNumber_active)}
              >
                1
              </span>
              <p>
                {(data.post?.resultHtml && headings.length > 0) ? (headings[0].text)
                  : data.post.content[0] && (data.post.content[0] as ArticleSection).heading}
              </p>
            </div>
          )}
        </div>
        <span className={classnames(styles.arrowIcon, { [styles.arrowIcon_open]: isOpen })}>
          <svg>
            <use
              href='/media/angle_down.svg#angleDown'
              xlinkHref='/media/angle_down.svg#angleDown'
            />
          </svg>
        </span>
      </button>
    )
  );
};

export default ArticleNavigation;
